import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import styled from "styled-components";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { SelectInput } from "../../../components/SelectInput";
import { Toggle } from "../../../components/Toggle";
import type { ImportMatchFields } from "../../../api/productsApi";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";

const CsvFormatRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x3}px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.textMuted};
  transition: transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AdvancedDetails = styled.details`
  margin-top: ${spacing.x3}px;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundLight};
  overflow: hidden;

  &[open] ${SummaryChevron} {
    transform: rotate(180deg);
  }
`;

const AdvancedSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  cursor: pointer;
  padding: ${spacing.x2}px ${spacing.x3}px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const AdvancedBody = styled.div`
  padding: 0 ${spacing.x3}px ${spacing.x3}px;
  border-top: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
`;

const AdvancedIntro = styled.p`
  margin: ${spacing.x2}px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textMuted};
`;

const MatchFieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

type EntityImportNs = "productImport" | "giftcardImport";

interface ImportOptionsProps {
  skipFirstRow: boolean;
  encoding: string;
  delimiter: string;
  onlyUpdate: boolean;
  onSkipFirstRowChange: (value: boolean) => void;
  onEncodingChange: (value: string) => void;
  onDelimiterChange: (value: string) => void;
  onOnlyUpdateChange: (value: boolean) => void;
  matchFields?: ImportMatchFields;
  onMatchFieldsChange?: (fields: ImportMatchFields) => void;
  showVariantMatchFields?: boolean;
  entityImportNs?: EntityImportNs;
}

export const ImportOptions = ({
  skipFirstRow,
  encoding,
  delimiter,
  onlyUpdate,
  onSkipFirstRowChange,
  onEncodingChange,
  onDelimiterChange,
  onOnlyUpdateChange,
  matchFields,
  onMatchFieldsChange,
  showVariantMatchFields = false,
  entityImportNs = "productImport",
}: ImportOptionsProps) => {
  const { t } = useTranslation();

  return (
    <AdvancedDetails>
      <AdvancedSummary>
        {t("import.sectionAdvancedOptionsSummary")}
        <SummaryChevron aria-hidden>
          <ChevronDown strokeWidth={2} />
        </SummaryChevron>
      </AdvancedSummary>
      <AdvancedBody>
        <AdvancedIntro>{t(`${entityImportNs}.sectionAdvancedOptionsIntro`)}</AdvancedIntro>
        <CsvFormatRow>
          <FormGroup>
            <FormLabel>{t("import.optionsEncoding")}</FormLabel>
            <SelectInput value={encoding} onChange={(e) => onEncodingChange(e.target.value)}>
              <option value="utf-8">{t("import.encodingUtf8")}</option>
              <option value="latin1">{t("import.encodingLatin1")}</option>
            </SelectInput>
          </FormGroup>

          <FormGroup>
            <FormLabel>{t("import.optionsDelimiter")}</FormLabel>
            <SelectInput value={delimiter} onChange={(e) => onDelimiterChange(e.target.value)}>
              <option value=",">{t("import.delimiterComma")}</option>
              <option value=";">{t("import.delimiterSemicolon")}</option>
            </SelectInput>
          </FormGroup>
        </CsvFormatRow>

        <FormGroup>
          <FormLabel>{t("import.optionsSkipFirstRow")}</FormLabel>
          <Toggle
            checked={skipFirstRow}
            onChange={onSkipFirstRowChange}
            label={t("import.optionsSkipFirstRowToggle")}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>{t(`${entityImportNs}.optionsOnlyUpdate`)}</FormLabel>
          <Toggle
            checked={onlyUpdate}
            onChange={onOnlyUpdateChange}
            label={t(`${entityImportNs}.optionsOnlyUpdateToggle`)}
          />
        </FormGroup>

        {matchFields && onMatchFieldsChange && (
          <FormGroup>
            <FormLabel>{t("productImport.matchFieldsLabel")}</FormLabel>
            <AdvancedIntro>{t("productImport.matchFieldsHelp")}</AdvancedIntro>
            <MatchFieldsStack>
              <Toggle
                checked={matchFields.id}
                onChange={(checked) => onMatchFieldsChange({ ...matchFields, id: checked })}
                label={t("productImport.matchFieldId")}
              />
              <Toggle
                checked={matchFields.articleNumber}
                onChange={(checked) =>
                  onMatchFieldsChange({ ...matchFields, articleNumber: checked })
                }
                label={t("productImport.matchFieldArticleNumber")}
              />
              <Toggle
                checked={matchFields.barcode}
                onChange={(checked) =>
                  onMatchFieldsChange({ ...matchFields, barcode: checked })
                }
                label={t("productImport.matchFieldBarcode")}
              />
              {showVariantMatchFields && (
                <>
                  <Toggle
                    checked={matchFields.variantId}
                    onChange={(checked) =>
                      onMatchFieldsChange({ ...matchFields, variantId: checked })
                    }
                    label={t("productImport.matchFieldVariantId")}
                  />
                  <Toggle
                    checked={matchFields.extraFields}
                    onChange={(checked) =>
                      onMatchFieldsChange({ ...matchFields, extraFields: checked })
                    }
                    label={t("productImport.matchFieldExtraFields")}
                  />
                </>
              )}
            </MatchFieldsStack>
          </FormGroup>
        )}
      </AdvancedBody>
    </AdvancedDetails>
  );
};
